import './OidcProviderSetup.scss';
import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { adminActions } from '@sonar-software/sonar-admin';
import { AsyncStateNotification } from '@ui/notifications';
import { createAsyncSelectors } from '@lib/selectors';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { AzureOidcProviderSetup, GoogleOidcProviderSetup, OidcProviderSetupSelector, OktaOidcProviderSetup, OneLoginOidcProviderSetup } from './oidc-providers';
import { securityActions } from '@sonar-software/sonar-security';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';
const getOidcConfigurationSelectors = createAsyncSelectors((state) => state.security.oidcConfigurations.byCustomer.byId.get);
const deleteOidcConfigurationSelectors = createAsyncSelectors((state) => state.admin.oidcConfigurations.byCustomer.byId.delete);
const updateOidcConfigurationSelectors = createAsyncSelectors((state) => state.admin.oidcConfigurations.byCustomer.byId.post);
export const OidcProviderSetup = (props) => {
    const { customerId, id, shardName, type = 'none' } = props;
    const [isErrored, setIsErrored] = useState(false);
    const oidcConfiguration = useSonarSelector(getOidcConfigurationSelectors.data);
    const oidcConfigurationIsRequested = useSonarSelector(getOidcConfigurationSelectors.isRequested);
    const getOidcConfiguration = useAction(securityActions.requestGetSecurityOidcConfigurationsByCustomerById);
    const oidcConfigurationIsDeleting = useSonarSelector(deleteOidcConfigurationSelectors.isRequested);
    const oidcConfigurationIsDeleted = useSonarSelector(deleteOidcConfigurationSelectors.isReceived);
    const deleteOidcConfiguration = useAction(adminActions.requestDeleteAdminOidcConfigurationsByCustomerById);
    const oidcConfigurationIsUpdating = useSonarSelector(updateOidcConfigurationSelectors.isRequested);
    const oidcConfigurationIsUpdated = useSonarSelector(updateOidcConfigurationSelectors.isReceived);
    const oidcConfigurationUpdateIsErrored = useSonarSelector(updateOidcConfigurationSelectors.isErrored);
    const oidcConfigurationUpdateIsFailed = useSonarSelector(updateOidcConfigurationSelectors.isFailed);
    const updateOidcConfiguration = useAction(adminActions.requestPostAdminOidcConfigurationsByCustomerById);
    const isLoading = oidcConfigurationIsRequested || oidcConfigurationIsDeleting || oidcConfigurationIsUpdating;
    const [displayedProvider, setDisplayedProvider] = useState(type);
    const [isEnteringNewData, setIsEnteringNewData] = useState(false);
    const handleProviderSelected = useCallback((provider) => {
        setDisplayedProvider(provider);
        setIsEnteringNewData(true);
    }, [setDisplayedProvider]);
    const handleProviderCleared = useCallback(() => {
        setDisplayedProvider('none');
        setIsEnteringNewData(false);
    }, [setDisplayedProvider, setIsEnteringNewData]);
    const handleProviderDeleted = useCallback(() => {
        deleteOidcConfiguration({
            customerId,
            shardName
        });
    }, [deleteOidcConfiguration, customerId, shardName]);
    const handleProviderUpdated = useCallback((configuration) => {
        updateOidcConfiguration({
            configuration,
            customerId,
            shardName
        });
    }, [updateOidcConfiguration, customerId, shardName]);
    const refreshOidcConfiguration = useCallback(() => {
        if (customerId) {
            getOidcConfiguration({
                customerId: customerId,
                shardName: shardName
            });
        }
    }, [getOidcConfiguration, customerId, shardName]);
    useEffect(() => {
        refreshOidcConfiguration();
    }, [refreshOidcConfiguration]);
    useEffect(() => {
        var _a;
        if (oidcConfiguration) {
            setIsEnteringNewData(false);
            setIsErrored(false);
            setDisplayedProvider((_a = oidcConfiguration === null || oidcConfiguration === void 0 ? void 0 : oidcConfiguration.provider) !== null && _a !== void 0 ? _a : 'none');
        }
        else {
            setIsEnteringNewData(true);
            setIsErrored(false);
            setDisplayedProvider('none');
        }
    }, [oidcConfiguration]);
    useEffect(() => {
        if (oidcConfigurationIsDeleted || oidcConfigurationIsUpdated) {
            setIsEnteringNewData(false);
            setIsErrored(false);
            refreshOidcConfiguration();
        }
    }, [oidcConfigurationIsDeleted, oidcConfigurationIsUpdated, refreshOidcConfiguration, customerId, shardName]);
    useEffect(() => {
        if (oidcConfigurationUpdateIsErrored || oidcConfigurationUpdateIsFailed) {
            setIsErrored(true);
        }
    }, [setIsErrored, oidcConfigurationUpdateIsErrored, oidcConfigurationUpdateIsFailed]);
    return (React.createElement("div", { className: 'oidc-provider-setup', id: id },
        React.createElement("div", { className: 'h2' }, ADMIN_DASHBOARD_COPY.OIDC_CONFIGURATON),
        React.createElement(LoadingSpinner, { spinning: isLoading },
            displayedProvider === 'none' &&
                React.createElement(OidcProviderSetupSelector, { onProviderSelected: handleProviderSelected }),
            displayedProvider === 'okta' &&
                React.createElement(OktaOidcProviderSetup, { isEnteringNewData: isEnteringNewData, isErrored: isErrored, oidcConfiguration: oidcConfiguration, onCleared: handleProviderCleared, onDeleted: handleProviderDeleted, onUpdated: handleProviderUpdated }),
            displayedProvider === 'azure' &&
                React.createElement(AzureOidcProviderSetup, { isEnteringNewData: isEnteringNewData, isErrored: isErrored, oidcConfiguration: oidcConfiguration, onCleared: handleProviderCleared, onDeleted: handleProviderDeleted, onUpdated: handleProviderUpdated }),
            displayedProvider === 'google' &&
                React.createElement(GoogleOidcProviderSetup, { isEnteringNewData: isEnteringNewData, isErrored: isErrored, oidcConfiguration: oidcConfiguration, onCleared: handleProviderCleared, onDeleted: handleProviderDeleted, onUpdated: handleProviderUpdated }),
            displayedProvider === 'onelogin' &&
                React.createElement(OneLoginOidcProviderSetup, { isEnteringNewData: isEnteringNewData, isErrored: isErrored, oidcConfiguration: oidcConfiguration, onCleared: handleProviderCleared, onDeleted: handleProviderDeleted, onUpdated: handleProviderUpdated })),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.UPDATE_OIDC_CONFIGURATION_ERRORED, failed: ADMIN_DASHBOARD_COPY.UPDATE_OIDC_CONFIGURATION_FAILED, stateSelector: updateOidcConfigurationSelectors.state }),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.DELETE_OIDC_CONFIG_ERRORED, failed: ADMIN_DASHBOARD_COPY.DELETE_OIDC_CONFIG_FAILED, stateSelector: deleteOidcConfigurationSelectors.state })));
};
